import { getExportById, getExportHistory } from 'api/v1/export';
import { downloadAttachment } from 'api/v2/shared/downloadDocument';
import { resolveExportError } from 'components/pages/DataExport/helpers';
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { DataExportCacheKeys } from 'types/cacheKeys.types';
import type { ExportEntry } from 'types/export';
import { onGenerateExportGTM } from 'utils/GTM';
import showToast from 'utils/toast';

const usePerformExport = <B>(
  request: (body: B) => Promise<{ exportId: string }>,
  onSuccess?: (exportResult?: ExportEntry['export']) => void,
): {
  mutate: (body: B) => void;
  exportId?: string;
  data?: ExportEntry;
  isLoading: boolean;
} => {
  const {
    mutate: originalMutate,
    data: { exportId } = {},
    reset,
  } = useMutation(request);

  const exportHistoryAPI = useQuery(['ExportHistory'], getExportHistory);

  const { data } = useQuery(
    [DataExportCacheKeys.ExportedDataFile, exportId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => getExportById(exportId!),
    {
      enabled: !!exportId,
      refetchInterval: (data) => {
        const { status } = data?.export || {};

        if (['failed', 'success'].includes(status as string)) {
          return false;
        }

        return 1000;
      },
      onSuccess: ({ export: exportResult }) => {
        const { status, linkToFile, error } = exportResult;
        if (status === 'success') {
          onSuccess?.(exportResult);

          onGenerateExportGTM();
          exportHistoryAPI.refetch();
          if (linkToFile) {
            downloadAttachment(
              linkToFile,
              new URL(linkToFile).pathname.split('/').slice(-1)[0] ??
                'Export.zip',
            ).finally(() => {
              setIsLoading(false);

              reset();
            });
          } else {
            setIsLoading(false);
          }
        }

        if (status === 'failed') {
          setIsLoading(false);
          exportHistoryAPI.refetch();
          reset();
          showToast(
            {
              title: resolveExportError(error),
            },
            { type: 'error' },
          );
        }
      },
      onError: (error: string) => {
        setIsLoading(false);
        reset();
        showToast({ title: resolveExportError(error) }, { type: 'error' });
      },
    },
  );

  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback<typeof originalMutate>(
    (...args) => {
      setIsLoading(true);
      originalMutate(...args);
    },
    [originalMutate],
  );

  return { mutate, exportId, data, isLoading };
};

export default usePerformExport;
